/**
 * @file Async thunks for getting and patching data for detailed state
 * @author Max van Loosbroek
 */

import { createAsyncThunk } from '@reduxjs/toolkit'
import { IHttpService } from 'angular'
import { RootState } from '../../../../common/redux/root.reducer'
import { MODULE_TYPES, URLS } from '../../../../common/utilities/constants/constants.module'
import { parseHttpError } from '../../../../common/utilities/errorHandling'
import {
  DetailedData,
  DetailedRobot,
  DetailedSubscriptionChain,
  DetailedTaas,
  LinkEntitiesBody,
  LinkTaas,
  InsertSubscriptionRequest,
  RobotAccount,
  DetailedRobotResponse,
  DetailedTaasResponse,
  SummarySubscription,
  DetailedSubscriptionChainResponse,
  PatchTaas,
  LinkTaasRequest,
  DetailedTaasOrder,
  HardwareType
} from '../../types/types'
import {
  chainPatchSelector,
  robotPatchSelector,
  taasPatchSelector,
  statusPatchSelector,
  detailedValidSelector,
  extendValidSelector
} from './selectors'
import { OldRelationDetailed, RelationDetailed } from '../../types/RelationDetailed'
import { mapChainDetailedResponseToChainDetailed, mapRobotDetailedResponseToRobotDetailed, mapTaasDetailedResponseToTaasDetailed } from '../../mapResponseType'

export const getDetailed = createAsyncThunk<
DetailedData,
{ serialId?: number, chainId?: number, taasId?: number },
{ extra: { $http: IHttpService } }
>('detailed/getDetailed', async ({ serialId, chainId, taasId }, thunkApi) => {
  // Get TaaS
  const taasUrl = `${URLS.admin.taas}/${taasId}`

  const { data: taasResponse } = taasId
    ? await thunkApi.extra.$http
      .get<DetailedTaasResponse>(taasUrl)
      .catch(parseHttpError)
    : { data: undefined }
  const urlSerialId = serialId ?? taasResponse?.dashboardRobot?.id
  // Get order
  // Will be updated
  const orderUrl = `${URLS.admin.taasOrders}`
  const order = taasId
    ? await thunkApi.extra.$http
      .get<DetailedTaasOrder[]>(orderUrl, {
      params: {
        'taasId[]': taasId
      }
    })
      .then(res => res.data[0])
      .catch(parseHttpError)
    : undefined

  // Get robot
  const robotUrl = `${URLS.admin.robots}/${urlSerialId}`

  const { data: robotResponse } = urlSerialId
    ? await thunkApi.extra.$http
      .get<DetailedRobotResponse>(robotUrl)
      .catch(parseHttpError)
    : { data: undefined }
  // Get robotAccount

  const robotAccountUrl = URLS.admin.robotAccount.replace('{robotId}', String(urlSerialId))

  const { data: robotAccount } = urlSerialId
    ? await thunkApi.extra.$http.get<RobotAccount>(robotAccountUrl).catch(e => {
      if (e?.status === 404) {
        // no account
        return { data: undefined }
      } else {
        return parseHttpError(e)
      }
    })
    : { data: undefined }
  // Get chain

  const chainUrl = `${URLS.admin.chains}/${chainId}`

  const { data: chainResponse } = chainId
    ? await thunkApi.extra.$http
      .get<DetailedSubscriptionChainResponse>(chainUrl)
      .catch(parseHttpError)
    : { data: undefined }

  const robot = mapRobotDetailedResponseToRobotDetailed(robotResponse, robotAccount)
  const taas = mapTaasDetailedResponseToTaasDetailed(taasResponse, order)
  let chain
  if (!taas) {
    chain = mapChainDetailedResponseToChainDetailed(chainResponse)
  }

  return {
    taas,
    robot,
    chain
  }
})

export const linkEntities = createAsyncThunk<
SummarySubscription | DetailedTaas,
LinkEntitiesBody,
{ extra: { $http: IHttpService } }
>('detailed/linkEntities', async (body, thunkApi) => {
  if (body.basic) {
    const url = URLS.admin.link
    const { data } = await thunkApi.extra.$http
      .post<SummarySubscription>(url, body.basic)
      .catch(parseHttpError)
    return data
  } else if (body.taas) {
    const {
      shippedAt,
      startAt,
      dashboardRobotId,
      taasId,
      taasNotes,
      robotNotes
    } = body.taas
    const url = URLS.admin.linkTaas.replace('{taasId}', taasId.toString())
    const requestBody: LinkTaasRequest = {
      dashboardRobotId,
      shippedAt,
      startAt
    }

    const { data } = await thunkApi.extra.$http
      .post<DetailedTaasResponse>(url, requestBody)
      .catch(parseHttpError)

    if (taasNotes) {
      const taasUrl = `${URLS.admin.taas}/${taasId}`
      const requestBody: PatchTaas = { notes: taasNotes }

      await thunkApi.extra.$http
        .patch<DetailedTaasResponse>(taasUrl, requestBody)
        .catch(parseHttpError)
    }
    if (robotNotes) {
      const robotUrl = `${URLS.admin.robotsV3}/${dashboardRobotId}`
      await thunkApi.extra.$http
        .patch<DetailedRobotResponse>(robotUrl, { notes: robotNotes })
        .catch(parseHttpError)
    }
    return mapTaasDetailedResponseToTaasDetailed(data)
  }
  throw Error('Invalid body')
})

export const linkTaasOrder = createAsyncThunk<
DetailedTaas,
LinkTaas,
{ extra: { $http: IHttpService } }
>('detailed/linkTaasOrder', async (body, thunkApi) => {
  const {
    shippedAt,
    startAt,
    dashboardRobotId,
    taasId,
    taasNotes,
    robotNotes
  } = body

  // Get taasOrderId from the taas.order.id
  const state = thunkApi.getState() as RootState
  const taasOrderId = state.detailed.taas?.order?.id

  if (!taasOrderId) {
    throw Error('TaaS Order ID is required when linking from orders')
  }

  // Use the new endpoint for orders
  const url = URLS.admin.linkTaasOrder.replace('{taasOrderId}', taasOrderId.toString())
  const requestBody: LinkTaasRequest = {
    dashboardRobotId,
    shippedAt,
    startAt
  }

  const { data } = await thunkApi.extra.$http
    .post<DetailedTaasResponse>(url, requestBody)
    .catch(parseHttpError)

  if (taasNotes) {
    const taasUrl = `${URLS.admin.taas}/${taasId}`
    const requestBody: PatchTaas = { notes: taasNotes }

    await thunkApi.extra.$http
      .patch<DetailedTaasResponse>(taasUrl, requestBody)
      .catch(parseHttpError)
  }
  if (robotNotes) {
    const robotUrl = `${URLS.admin.robotsV3}/${dashboardRobotId}`
    await thunkApi.extra.$http
      .patch<DetailedRobotResponse>(robotUrl, { notes: robotNotes })
      .catch(parseHttpError)
  }
  return mapTaasDetailedResponseToTaasDetailed(data)
})

export const extendSub = createAsyncThunk<
SummarySubscription[],
{subBody: InsertSubscriptionRequest, chainId: number},
{ state: RootState, extra: { $http: IHttpService } }
>('detailed/addSubs', async (body, thunkApi) => {
  const url = `${URLS.admin.chains}/${body.chainId}/subscriptions`
  const { detailed } = thunkApi.getState()
  if (!extendValidSelector(detailed).detailedValid) {
    throw Error(`Invalid form: ${extendValidSelector(detailed).errorMessage}`)
  }
  const { data } = await thunkApi.extra.$http
    .post<SummarySubscription[]>(url, body.subBody)
    .catch(parseHttpError)
  await thunkApi.dispatch(getDetailed({ chainId: body.chainId }))
  return data
})

export const searchSubscriptions = createAsyncThunk<
SummarySubscription[],
{ relationId: string, hardwareType?: HardwareType },
{ extra: { $http: IHttpService } }
>('detailed/searchSubscriptions', async ({ relationId, hardwareType }, thunkApi) => {
  const url = `${URLS.admin.subscriptions}?relationId=${relationId}${hardwareType ? `&hardwareType=${hardwareType}` : ''}`
  const { data: subs } =
  relationId
    ? await thunkApi.extra.$http
      .get<SummarySubscription[]>(url)
      .catch(parseHttpError)
    : { data: [] as SummarySubscription[] }
  return subs
})

export const searchRobots = createAsyncThunk<
DetailedRobot[],
{ serial?: string, boxNumber?: string, hardwareType?: HardwareType },
{ extra: { $http: IHttpService } }
>('detailed/searchRobots', async ({ serial, boxNumber, hardwareType }, thunkApi) => {
  const url = boxNumber
    ? `${URLS.admin.robots}?boxNr=${boxNumber}${hardwareType ? `&hardwareType=${hardwareType}` : ''}`
    : `${URLS.admin.robots}?serial=${serial ? serial.replace('T-', '') : ''}${hardwareType ? `&hardwareType=${hardwareType}` : ''}` // remove taas indicator
  const { data: robots } = serial || boxNumber
    ? await thunkApi.extra.$http
      .get<DetailedRobotResponse[]>(url)
      .catch(parseHttpError)
    : { data: [] as DetailedRobot[] }

  const mappedData = robots.map((rob) => mapRobotDetailedResponseToRobotDetailed(rob))
  return mappedData
})

export const searchRelations = createAsyncThunk<
RelationDetailed[],
{ name: string },
{ extra: { $http: IHttpService } }
>('detailed/searchRelations', async ({ name }, thunkApi) => {
  const url = `${URLS.admin.relations}?name=${name}`
  const { data: relations } = name
    ? await thunkApi.extra.$http
      .get<OldRelationDetailed[]>(url)
      .catch(parseHttpError)
    : { data: [] as OldRelationDetailed[] }
  return relations.map((r) => ({ id: r.relationId, relationType: r.relationType, name: r.name, teamleaderId: r.teamleaderId, teamleaderType: r.teamleaderType, type: r.type, isCustomer: r.isCustomer }))
})

export const searchTaas = createAsyncThunk<
DetailedTaas[],
{ id: string, hardwareType?: HardwareType },
{ extra: { $http: IHttpService } }
>('detailed/searchTaas', async ({ id, hardwareType }, thunkApi) => {
  const url = `${URLS.admin.taas}?relationId=${id}&hasRobot=false${hardwareType ? `&hardwareType=${hardwareType}` : ''}`
  const { data: taas } = id
    ? await thunkApi.extra.$http.get<DetailedTaasResponse[]>(url).catch(parseHttpError)
    : { data: [] as DetailedTaasResponse[] }
  return taas.map((t) => mapTaasDetailedResponseToTaasDetailed(t))
})

export const patchDetailed = createAsyncThunk<
{
  robot?: DetailedRobot
  chain?: DetailedSubscriptionChain
  taas?: DetailedTaas
},
void,
{ state: RootState, extra: { $http: IHttpService } }
>('detailed/patchDetailed', async (_, thunkApi) => {
  const { detailed } = thunkApi.getState()
  const { robot: { id: serialId, account } = {} } = detailed
  const { chain: { id: chainId } = {} } = detailed
  const { taas: { id: taasId } = {} } = detailed
  const chainPatch = chainPatchSelector(detailed)
  const robotPatch = robotPatchSelector(detailed)
  const taasPatch = taasPatchSelector(detailed)
  const statusPatch = statusPatchSelector(detailed)
  const result: {
    robot?: DetailedRobot
    chain?: DetailedSubscriptionChain
    taas?: DetailedTaas
  } = {}
  if (!detailedValidSelector(detailed).detailedValid) {
    throw Error(`Invalid form: ${detailedValidSelector(detailed).errorMessage}`)
  }
  if (statusPatch && account) {
    const statusUrl = URLS.admin.robotStatus.replace(
      '{robotId}',
    `${account.robotId}`
    )
    await thunkApi.extra.$http
      .post<RobotAccount>(statusUrl, statusPatch)
      .catch(parseHttpError)
  }
  if (chainPatch) {
    const chainUrl = `${URLS.admin.chains}/${chainId}`

    const { data: chainResponse } = await thunkApi.extra.$http
      .patch<DetailedSubscriptionChainResponse>(chainUrl, chainPatch)
      .catch(parseHttpError)
    result.chain = mapChainDetailedResponseToChainDetailed(chainResponse)
  }
  if (taasPatch) {
    const taasUrl = `${URLS.admin.taas}/${taasId}`
    const { data: taasResponse } =
    await thunkApi.extra.$http
      .patch<DetailedTaasResponse>(taasUrl, taasPatch)
      .catch(parseHttpError)
    result.taas = mapTaasDetailedResponseToTaasDetailed(taasResponse)
  }
  if (detailed.taas?.delete) {
    const taasUrl = `${URLS.admin.taasDelete}/${taasId}`
    await thunkApi.extra.$http.delete(taasUrl).catch(parseHttpError)
    result.taas = undefined
  }
  if (robotPatch) {
    const robotUrl = `${URLS.admin.robotsV3}/${serialId}`
    const robotResponse =
    await thunkApi.extra.$http
      .patch<DetailedRobotResponse>(robotUrl, robotPatch)
      .then(res => res.data)
      .catch(parseHttpError)
    result.robot = mapRobotDetailedResponseToRobotDetailed(robotResponse)
  }
  return result
})

export const getSubscriptionModuleStatus = createAsyncThunk<
{ enabled: boolean },
({ taasId: number } | { chainId: number }) & { moduleType?: string },
{ extra: { $http: IHttpService } }
>('detailed/getSubscriptionModuleStatus', async (params, thunkApi) => {
    const { moduleType = MODULE_TYPES.beeldzorg } = params
    const query = 'taasId' in params ? `taasId=${params.taasId}` : `chainId=${params.chainId}`
    const url = `${URLS.admin.subscriptionModules}?${query}&moduleType=${moduleType}`
    try {
      const response = await thunkApi.extra.$http.get<{ items: Array<{ enabled: boolean }> }>(url)
      const enabled = response?.data?.items?.[0]?.enabled ?? false
      return { enabled }
    } catch {
      return { enabled: false }
    }
})
